// © 2024–2026 Jos Jozef Sytze van der Werf — Alle rechten voorbehouden.
import { useState, useEffect } from 'react'
import { Users, Plus, ShieldCheck, Trash2, Copy, CheckCircle2, X,
         ChevronDown, ChevronUp, AlertTriangle, ArrowUpCircle } from 'lucide-react'
import { ModuleShell }  from '@/components/ui/ModuleShell'
import { Button, Input, Select, Toggle } from '@/components/ui'
import { InfoBlock } from '@/components/ui/InfoBlock'
import { Card }         from '@/components/ui/Card'
import { RadioGroup }   from '@/components/ui/RadioGroup'
import { Badge }        from '@/components/ui/Badge'
import { EmptyState }   from '@/components/feedback/EmptyState'
import { LoadingState } from '@/components/feedback/LoadingState'
import { AlertBanner }  from '@/components/feedback/AlertBanner'
import { useAuthStore } from '@/store'
import { BackButton }  from '@/components/ui/BackButton'
import { ClosingGuidance } from '@/components/process/ClosingGuidance'
import { VP_CONFIGURABLE_MODULES, VP_ALWAYS_VISIBLE_MODULES, VP_NEVER_VISIBLE_MODULES } from '@/services/permission'
import { useToast }     from '@/hooks/useToast'
import {
  loadTrustedPersons, createTrustedPerson, revokeTrustedPerson,
  type TrustedPersonConfig, type CreateTrustedPersonParams,
} from '@/services/trustedPerson'
import { promoteTrustedPerson } from '@/services/trustedPerson/promote'
import { loadOwnerDob } from '@/services/profile'
import { TrustedPersonRelationship, ActivationTrigger, TrustedPersonRole } from '@/types'
import { saveModuleProgress } from '@/services/profile'

const RELATIONSHIP_OPTIONS = [
  { value: TrustedPersonRelationship.PARTNER, label: 'Partner / Echtgenoot·e' },
  { value: TrustedPersonRelationship.CHILD,   label: 'Kind' },
  { value: TrustedPersonRelationship.SIBLING, label: 'Broer of zus' },
  { value: TrustedPersonRelationship.FRIEND,  label: 'Vriend of vriendin' },
  { value: TrustedPersonRelationship.OTHER,   label: 'Overig' },
]
const ACTIVATION_OPTIONS = [
  { value: ActivationTrigger.ON_DEATH,  label: 'Alleen na mijn overlijden', description: 'Standaard en meest gebruikelijk.' },
  { value: ActivationTrigger.IMMEDIATE, label: 'Direct actief',             description: 'Mijn vertrouwenspersoon kan direct meekijken in geselecteerde onderdelen.' },
]

interface WizardForm {
  firstName: string; relationship: string; email: string
  activationTrigger: string; role: string
  moduleFuneralWishes: boolean; modulePersonalFarewell: boolean
  moduleMedicalWishes: boolean; moduleDocumentIndex: boolean
  moduleMessages: boolean; moduleDigitalEstate: boolean; moduleLifeStory: boolean
}
const EMPTY_FORM: WizardForm = {
  firstName: '', relationship: '', email: '',
  activationTrigger: ActivationTrigger.ON_DEATH, role: TrustedPersonRole.PRIMARY,
  moduleFuneralWishes: true, modulePersonalFarewell: false,
  moduleMedicalWishes: false, moduleDocumentIndex: true,
  moduleMessages: true, moduleDigitalEstate: false, moduleLifeStory: false,
}

// ── Inline bevestigingsdialoog - vervangt window.confirm() (werkt niet op iOS PWA) ──
interface ConfirmDialogProps {
  message: string
  onConfirm: () => void
  onCancel: () => void
}
function ConfirmDialog({ message, onConfirm, onCancel }: ConfirmDialogProps) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-sm bg-white rounded-2xl shadow-2xl p-6 flex flex-col gap-4">
        <p className="text-sm text-ink leading-relaxed">{message}</p>
        <div className="flex gap-3">
          <Button variant="secondary" fullWidth onClick={onCancel}>Annuleren</Button>
          <Button fullWidth onClick={onConfirm}>Bevestigen</Button>
        </div>
      </div>
    </div>
  )
}

// ── Overdrachtsbrief modal ────────────────────────────────────────────────────
interface BriefModalProps {
  person:     TrustedPersonConfig
  ownerName:  string
  onClose:    () => void
}
function BriefModal({ person, ownerName, onClose }: BriefModalProps) {
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 p-4 overflow-y-auto print:bg-white print:p-0 print:block">
      <div className="w-full max-w-lg bg-white rounded-2xl shadow-2xl print:shadow-none print:rounded-none print:max-w-none">
        <style>{`@media print { body > *:not(.wilsapp-brief-wrapper) { display: none !important; } .wilsapp-brief-wrapper { display: block !important; position: fixed; top: 0; left: 0; width: 100%; } .no-print { display: none !important; } .brief-content { padding: 40px !important; font-size: 14px !important; } }`}</style>
        <div className="wilsapp-brief-wrapper">
          <div className="no-print flex items-center justify-between px-6 pt-5 pb-2">
            <p className="text-sm font-semibold text-ink">Overdrachtsbrief - {person.firstName}</p>
            <button onClick={onClose} className="text-ink-4 hover:text-ink-3"><X className="h-4 w-4" /></button>
          </div>
          <div className="brief-content px-8 py-6 text-sm text-ink leading-relaxed flex flex-col gap-4">
            <div className="flex flex-col gap-1">
              <p className="text-xs text-ink-4">{new Date().toLocaleDateString('nl-NL', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
              <p className="text-lg font-semibold text-ink">Aan: {person.firstName}</p>
              <p className="text-xs text-ink-4">
                Rol: {person.role === TrustedPersonRole.PRIMARY ? 'Eerste vertrouwenspersoon' : 'Tweede vertrouwenspersoon'}
              </p>
            </div>
            <p className="text-ink-2 leading-relaxed">
              Ik heb mijn wensen en instructies vastgelegd in WilsApp, een hulpmiddel voor nabestaanden.
              U heeft eerder een sleutelcode van mij ontvangen. Met die code en mijn geboortedatum kunt u deze informatie inzien wanneer dat nodig is.
            </p>
            <div className="bg-sand rounded-xl px-4 py-3">
              <p className="text-xs font-semibold text-ink mb-1">Hoe gebruikt u uw sleutelcode?</p>
              <ol className="flex flex-col gap-1 text-sm text-ink-2 list-decimal list-inside">
                <li>Ga naar <strong>wilsapp.nl/toegang</strong></li>
                <li>Voer uw sleutelcode in (die u eerder van mij heeft ontvangen)</li>
                <li>Voer mijn geboortedatum in</li>
              </ol>
            </div>
            <div className="bg-sand rounded-xl px-4 py-3">
              <p className="text-xs font-semibold text-ink mb-1">Wanneer te gebruiken</p>
              <p className="text-sm text-ink-2">
                {person.activationTrigger === 'IMMEDIATE'
                  ? 'U kunt uw sleutelcode direct gebruiken.'
                  : 'Gebruik uw sleutelcode na mijn overlijden.'}
              </p>
            </div>
            <div className="border-t border-sand-200 pt-4">
              <p className="text-xs font-semibold text-ink mb-1">Bewaarinstructie</p>
              <p className="text-sm text-ink-2">Bewaar deze brief op een veilige, persoonlijke plek. Geef de code niet door aan anderen.</p>
            </div>
            <div className="border-t border-sand-200 pt-4 flex flex-col gap-0.5">
              <p className="text-sm font-semibold text-ink">{ownerName || 'Uw naaste'}</p>
              <p className="text-xs text-ink-4">Opgesteld via WilsApp - wilsapp.nl</p>
              <p className="text-xs text-ink-4">Datum: {new Date().toLocaleDateString('nl-NL', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
            </div>
          </div>
          <div className="no-print px-8 pb-6 flex gap-3">
            <Button variant="secondary" onClick={onClose} fullWidth>Sluiten</Button>
            <Button fullWidth onClick={() => window.print()}>Afdrukken</Button>
          </div>
        </div>
      </div>
    </div>
  )
}

export function TrustedPersonPage() {
  const { userId } = useAuthStore()
  const { toast }  = useToast()
  const [persons,      setPersons]      = useState<TrustedPersonConfig[]>([])
  const [isLoading,    setIsLoading]    = useState(true)
  const [showWiz,      setShowWiz]      = useState(false)
  const [step,         setStep]         = useState(1)
  const [form,         setForm]         = useState<WizardForm>(EMPTY_FORM)
  const [errors,       setErrors]       = useState<Partial<WizardForm>>({})
  const [isSaving,     setIsSaving]     = useState(false)
  const [generatedCode, setGeneratedCode] = useState<string | null>(null)
  const [codeCopied,   setCodeCopied]   = useState(false)
  const [dobForHash,   setDobForHash]   = useState('')
  const [ownerName,    setOwnerName]    = useState('')
  const [showPermissions, setShowPermissions] = useState<string | null>(null)
  // Inline confirm dialogen - vervangt window.confirm()
  const [confirmRevoke,  setConfirmRevoke]  = useState<{ id: string; name: string } | null>(null)
  const [confirmPromote, setConfirmPromote] = useState<{ id: string; name: string } | null>(null)
  // Overdrachtsbrief
  const [briefPerson, setBriefPerson] = useState<TrustedPersonConfig | null>(null)

  async function reload() {
    if (!userId) return
    setPersons(await loadTrustedPersons(userId))
    setIsLoading(false)
  }
  useEffect(() => {
    if (!userId) return
    reload()
    loadOwnerDob(userId).then(dob => setDobForHash(dob))
    // Laad naam eigenaar voor overdrachtsbrief
    import('@/services/profile').then(({ getFirstProfile }) => {
      getFirstProfile().then(p => { if (p?.firstName) setOwnerName(p.firstName) })
    })
  }, [userId])

  function validateStep() {
    const e: Partial<WizardForm> = {}
    if (step === 1) {
      if (!form.firstName.trim()) e.firstName = 'Voornaam is verplicht'
      if (!form.relationship) e.relationship = 'Kies een relatie'
    }
    setErrors(e); return Object.keys(e).length === 0
  }

  function nextStep() { if (!validateStep()) return; setStep(s => s + 1) }
  function prevStep() { setStep(s => s - 1); setErrors({}) }

  async function handleCreate() {
    if (!userId) return
    if (!dobForHash) {
      toast.error('Vul eerst uw geboortedatum in bij Persoonlijke gegevens.')
      return
    }
    setIsSaving(true)
    try {
      const result = await createTrustedPerson({
        userId,
        dateOfBirth: dobForHash,
        firstName: form.firstName,
        relationship: form.relationship as TrustedPersonRelationship,
        email: form.email,
        activationTrigger: form.activationTrigger as ActivationTrigger,
        role: form.role as TrustedPersonRole,
        permissions: {
          moduleFuneralWishes:    form.moduleFuneralWishes,
          modulePersonalFarewell: form.modulePersonalFarewell,
          moduleMedicalWishes:    form.moduleMedicalWishes,
          moduleDocumentIndex:    form.moduleDocumentIndex,
          moduleMessages:         form.moduleMessages,
          moduleDigitalEstate:    form.moduleDigitalEstate,
          moduleLifeStory:        form.moduleLifeStory,
        },
      } as CreateTrustedPersonParams)
      setGeneratedCode(result.displayCode)
      setStep(4)
      await saveModuleProgress(userId, 'trusted_person', 6, 6)
      await reload()
    } catch (err) {
      console.error(err); toast.error('Aanmaken mislukt. Probeer opnieuw.')
    } finally { setIsSaving(false) }
  }

  function resetWizard() {
    setForm(EMPTY_FORM); setStep(1); setErrors({})
    setGeneratedCode(null); setCodeCopied(false); setShowWiz(false)
  }

  // Revoke - via inline dialoog
  async function executeRevoke(id: string, name: string) {
    await revokeTrustedPerson(id)
    toast.success(`${name} verwijderd als vertrouwenspersoon`)
    setConfirmRevoke(null)
    await reload()
  }

  // Promote - via inline dialoog
  async function executePromote(reserveId: string) {
    if (!userId) return
    const result = await promoteTrustedPerson(reserveId, userId)
    if (result.success) {
      toast.success(result.message)
    } else {
      toast.error(result.message)
    }
    setConfirmPromote(null)
    await reload()
  }

  function copyCode() {
    if (!generatedCode) return
    navigator.clipboard.writeText(generatedCode.replace('–', '-'))
      .then(() => { setCodeCopied(true); setTimeout(() => setCodeCopied(false), 2000) })
  }

  const active = persons.filter(p => p.confirmationStatus !== 'REVOKED')
  const st = active.length === 0 ? 'NOT_STARTED' : 'COMPLETE'

  // Veiligheidscheck: nooit twee PRIMARY's tonen
  const activePrimaries = active.filter(p => p.role === TrustedPersonRole.PRIMARY)
  if (activePrimaries.length > 1) {
    console.warn('[WilsApp] Meerdere PRIMARY VP records gedetecteerd - datainconsistentie')
  }

  if (isLoading) return <LoadingState message="Laden..." />

  return (
    <div>
      <BackButton />
      <ModuleShell
        title="Vertrouwenspersoon"
        description="Wijs iemand aan die u met een persoonlijke sleutelcode toegang geeft tot de onderdelen die u deelt, nu u zelf bepaalt wie u vertrouwt. Een vertrouwenspersoon is geen wettelijk rechtsfiguur; WilsApp controleert geen overlijden."
        status={st as 'NOT_STARTED'|'IN_PROGRESS'|'COMPLETE'}
        actions={!showWiz && active.length < 2
          ? <Button size="sm" onClick={() => setShowWiz(true)} icon={<Plus className="h-4 w-4" />}>Aanwijzen</Button>
          : undefined}
      >
        <InfoBlock title="Waarom dit nu vastleggen?" variant="neutral" defaultOpen={false}>
          <p>In de praktijk lopen nabestaanden vaak vast op één vraag: wie mag dit eigenlijk regelen? Met een vertrouwenspersoon is dat van tevoren beslist. Mijn advies: kies er liefst twee, voor het geval de eerste er net niet is.</p>
          <p>Laat de personen ook even weten dat u hen heeft gekozen. Dit is iets anders dan een volmacht bij de notaris; voor uw geldzaken en medische beslissingen regelt u dat apart, in een levenstestament.</p>
        </InfoBlock>

        {/* Wizard */}
        {showWiz && (
          <Card className="border-sage-200">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-base font-semibold text-ink">
                {step === 1 && 'Stap 1 - Gegevens'}
                {step === 2 && 'Stap 2 - Activatiemoment'}
                {step === 3 && 'Stap 3 - Toegang'}
                {step === 4 && 'Sleutelcode'}
              </h2>
              <button onClick={resetWizard} className="text-ink-4 hover:text-ink-3"><X className="h-4 w-4" /></button>
            </div>
            <div className="flex gap-1 mb-5">
              {[1,2,3].map(s => (
                <div key={s} className={`flex-1 h-1 rounded-full transition-all ${step >= s ? 'bg-moss' : 'bg-sand-300'}`} />
              ))}
            </div>

            {step === 1 && (
              <div className="flex flex-col gap-4">
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-medium text-ink-4 uppercase tracking-wider">Rol</label>
                  <div className="grid grid-cols-2 gap-2">
                    {[{ v: TrustedPersonRole.PRIMARY, l: 'Eerste' }, { v: TrustedPersonRole.RESERVE, l: 'Tweede' }].map(r => (
                      <button key={r.v} onClick={() => setForm(p => ({ ...p, role: r.v }))}
                        className={`rounded-xl border-2 px-3 py-2 text-sm font-medium transition-all ${form.role === r.v ? 'border-sage-600 bg-sage-50 text-sage-700' : 'border-sand-300 text-ink-3 hover:border-sage-300'}`}>
                        {r.l}
                      </button>
                    ))}
                  </div>
                </div>
                <Input label="Voornaam" value={form.firstName} onChange={e => setForm(p => ({ ...p, firstName: e.target.value }))} error={errors.firstName} required autoFocus />
                <Select label="Relatie" options={RELATIONSHIP_OPTIONS} value={form.relationship} onChange={e => setForm(p => ({ ...p, relationship: e.target.value }))} placeholder="Kies relatie..." error={errors.relationship} />
                <Input label="E-mailadres (optioneel)" type="email" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} hint="Alleen voor jaarlijkse herinnering als u dat wilt." />
                <div className="flex gap-3 pt-1">
                  <Button fullWidth onClick={nextStep}>Volgende</Button>
                  <Button variant="ghost" onClick={resetWizard}>Annuleren</Button>
                </div>
              </div>
            )}

            {step === 2 && (
              <div className="flex flex-col gap-4">
                <RadioGroup name="activation" options={ACTIVATION_OPTIONS} value={form.activationTrigger} onChange={v => setForm(p => ({ ...p, activationTrigger: v }))} />
                <div className="flex gap-3 pt-1">
                  <Button variant="secondary" onClick={prevStep}>Terug</Button>
                  <Button fullWidth onClick={nextStep}>Volgende</Button>
                </div>
              </div>
            )}

            {step === 3 && (
              <div className="flex flex-col gap-3">
                <p className="text-sm text-ink-3 mb-1">Kies welke onderdelen u met uw vertrouwenspersoon deelt.</p>
                {VP_ALWAYS_VISIBLE_MODULES.filter(m => m.key !== 'moduleEmergencyCard').map(({ key, label }) => (
                  <div key={key} className="flex items-center justify-between rounded-xl bg-sage-50 border border-sage-200 px-4 py-2.5">
                    <span className="text-sm font-medium text-sage-800">{label}</span>
                    <span className="text-xs text-sage-600 font-medium">Altijd zichtbaar</span>
                  </div>
                ))}
                {VP_CONFIGURABLE_MODULES.map(({ key, label }) => (
                  <div key={key} className="flex items-center justify-between rounded-xl bg-sand px-4 py-2.5">
                    <span className="text-sm text-ink-2">{label}</span>
                    <Toggle label="" checked={form[key as keyof WizardForm] as boolean} onChange={v => setForm(p => ({ ...p, [key]: v }))} />
                  </div>
                ))}
                <div className="mt-1">
                  <p className="text-xs font-semibold text-ink-4 uppercase tracking-wider mb-2">Altijd afgeschermd</p>
                  {VP_NEVER_VISIBLE_MODULES.map(({ key, label, reason }) => (
                    <div key={key} className="flex items-start gap-3 rounded-xl bg-sand border border-sand-300 px-4 py-2.5 mb-1.5 opacity-60">
                      <div className="flex-1">
                        <span className="text-sm text-ink-3">{label}</span>
                        <p className="text-xs text-ink-4 mt-0.5">{reason}</p>
                      </div>
                      <span className="text-xs text-ink-4 font-medium mt-0.5 flex-shrink-0">Nooit</span>
                    </div>
                  ))}
                </div>
                <div className="flex gap-3 pt-2">
                  <Button variant="secondary" onClick={prevStep}>Terug</Button>
                  <Button fullWidth onClick={handleCreate} loading={isSaving} disabled={!dobForHash}>
                    Sleutelcode aanmaken
                  </Button>
                  {!dobForHash && (
                    <p className="text-xs text-status-alertText mt-2">
                      Vul eerst uw geboortedatum in bij <strong>Persoonlijke gegevens</strong>.
                    </p>
                  )}
                </div>
              </div>
            )}

            {step === 4 && generatedCode && (
              <div className="flex flex-col items-center text-center gap-4">
                <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-moss">
                  <ShieldCheck className="h-7 w-7 text-white" />
                </div>
                <div>
                  <p className="text-sm font-medium text-ink mb-1">Uw sleutelcode</p>
                  <div className="text-3xl font-mono font-bold text-ink tracking-widest px-6 py-4 bg-sage-50 rounded-2xl border-2 border-sage-200 select-all">
                    {generatedCode}
                  </div>
                </div>
                <button onClick={copyCode} className="flex items-center gap-1.5 text-sm text-sage-600 hover:text-sage-700 transition-colors">
                  {codeCopied ? <CheckCircle2 className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                  {codeCopied ? 'Gekopieerd' : 'Code kopiëren'}
                </button>
                <AlertBanner variant="warning"
                  message={`Bewaar deze code veilig en geef hem persoonlijk aan ${form.firstName}. De code wordt niet opgeslagen en kan niet worden hersteld.`} />
                <Button fullWidth onClick={resetWizard}>Klaar - terug naar overzicht</Button>
              </div>
            )}
          </Card>
        )}

        {/* Empty state */}
        {active.length === 0 && !showWiz && (
          <EmptyState icon={<Users className="h-8 w-8" />}
            title="Nog geen vertrouwenspersoon aangewezen"
            description="Wijs iemand aan die u via een persoonlijke sleutelcode toegang geeft tot de onderdelen die u deelt."
            action={{ label: 'Vertrouwenspersoon aanwijzen', onClick: () => setShowWiz(true) }} />
        )}

        {/* Bestaande personen */}
        <div className="flex flex-col gap-3">
          {active.map(person => (
            <Card key={person.id} padding="sm">
              <div className="flex items-start gap-3">
                <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-sage-50 text-sage-600">
                  <Users className="h-5 w-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-semibold text-ink">{person.firstName}</p>
                    <Badge variant={person.role === 'PRIMARY' ? 'default' : 'muted'} size="sm">
                      {person.role === 'PRIMARY' ? 'Eerste' : 'Tweede'}
                    </Badge>
                    <Badge variant="success" size="sm">Actief</Badge>
                  </div>
                  <p className="text-xs text-ink-4 mt-0.5">
                    {RELATIONSHIP_OPTIONS.find(r => r.value === person.relationship)?.label ?? person.relationship}
                    {' · '}
                    {ACTIVATION_OPTIONS.find(a => a.value === person.activationTrigger)?.label ?? person.activationTrigger}
                  </p>
                  <button
                    onClick={() => setShowPermissions(showPermissions === person.id ? null : person.id)}
                    className="flex items-center gap-1 mt-1.5 text-xs text-sage-600 hover:text-sage-700"
                  >
                    {showPermissions === person.id ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                    Toegang bekijken
                  </button>
                  {showPermissions === person.id && (
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {VP_CONFIGURABLE_MODULES.map(({ key, label }) => person[key as keyof TrustedPersonConfig]
                        ? <Badge key={key} variant="success" size="sm">{label}</Badge>
                        : null
                      )}
                    </div>
                  )}
                </div>
                {/* Acties: overdrachtsbrief | maak primair (reserve only) | verwijderen */}
                <div className="flex items-center gap-1 flex-shrink-0">
                  <button
                    title="Overdrachtsbrief afdrukken"
                    onClick={() => setBriefPerson(person)}
                    className="flex h-7 w-7 items-center justify-center rounded-lg text-sage-600 hover:bg-sage-50 transition-colors"
                  >
                    <ShieldCheck className="h-3.5 w-3.5" />
                  </button>
                  {person.role === TrustedPersonRole.RESERVE && (
                    <button
                      title="Maak primaire vertrouwenspersoon"
                      onClick={() => setConfirmPromote({ id: person.id, name: person.firstName })}
                      className="flex h-7 w-7 items-center justify-center rounded-lg text-sage-600 hover:bg-sage-50 transition-colors"
                    >
                      <ArrowUpCircle className="h-3.5 w-3.5" />
                    </button>
                  )}
                  <button
                    title="Verwijderen"
                    onClick={() => setConfirmRevoke({ id: person.id, name: person.firstName })}
                    className="flex h-7 w-7 items-center justify-center rounded-lg text-ink-4 hover:bg-status-alertBg hover:text-status-alertText transition-colors"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            </Card>
          ))}
        </div>

        {/* Privacy noot */}
        {active.length > 0 && (
          <div className="flex flex-col gap-2">
            <div className="flex items-start gap-2.5 rounded-xl bg-status-warnBg border border-status-warnBorder px-4 py-3">
              <AlertTriangle className="h-4 w-4 text-status-warnText flex-shrink-0 mt-0.5" />
              <p className="text-xs text-status-warnText leading-relaxed">
                <strong>Heeft u deze vertrouwenspersoon vóór versie V35 aangemaakt?</strong>{' '}
                Verwijder deze dan en maak een nieuwe aan.
              </p>
            </div>
            <div className="flex items-start gap-2.5 rounded-xl bg-sage-50 border border-sage-100 px-4 py-3">
              <AlertTriangle className="h-4 w-4 text-sage-600 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-sage-700 leading-relaxed">
                Uw vertrouwenspersoon heeft toegang via een sleutelcode die u persoonlijk heeft overhandigd.
                WilsApp bewaart de code nooit in leesbare vorm.
              </p>
            </div>
          </div>
        )}

        {active.length > 0 && (
          <ClosingGuidance title="Uw vertrouwenspersonen zijn vastgelegd">
            Bespreek uw keuzes regelmatig met hen en controleer af en toe of deze nog aansluiten bij uw situatie.
          </ClosingGuidance>
        )}

        <BackButton variant="bottom" />
      </ModuleShell>

      {/* Inline bevestiging: verwijderen */}
      {confirmRevoke && (
        <ConfirmDialog
          message={`Weet u zeker dat u ${confirmRevoke.name} als vertrouwenspersoon wilt verwijderen? De sleutelcode wordt ongeldig.`}
          onConfirm={() => executeRevoke(confirmRevoke.id, confirmRevoke.name)}
          onCancel={() => setConfirmRevoke(null)}
        />
      )}

      {/* Inline bevestiging: promoveren */}
      {confirmPromote && (
        <ConfirmDialog
          message={`${confirmPromote.name} promoveren tot primaire vertrouwenspersoon? De huidige primaire VP wordt dan reserve.`}
          onConfirm={() => executePromote(confirmPromote.id)}
          onCancel={() => setConfirmPromote(null)}
        />
      )}

      {/* Overdrachtsbrief modal */}
      {briefPerson && (
        <BriefModal
          person={briefPerson}
          ownerName={ownerName}
          onClose={() => setBriefPerson(null)}
        />
      )}
    </div>
  )
}
